// Money formatting — single source of truth for how amounts are rendered.
//
// The shell follows the project-wide convention of putting the currency
// symbol *after* the number (`123.45$`), matching Playmer's in-game UI.
// Two decimal places by default — reasonable for $ / € / £; pass
// `decimals: 0` for integer-only currencies (¥, etc.) or for counters
// that track integer units (autospins, freespins) — use `formatCount`
// for those instead.
//
// Thousand separators come from `toLocaleString`, which respects the
// browser's locale. If the shell ever needs to lock formatting to a
// specific locale, pass it through a second arg here rather than
// sprinkling locale strings across the components.

export interface FormatMoneyOptions {
  /** Number of decimal places. Defaults to 2. */
  decimals?: number;
  /** BCP-47 locale override (e.g. 'en-US'). Defaults to browser locale. */
  locale?: string;
}

// `Intl.NumberFormat` instances are somewhat expensive to construct (they
// parse/resolve locale data every time) and `toLocaleString` builds one
// internally on every call. WinBar's count-up animation calls `formatMoney`
// once per animation frame, so a fresh formatter per call adds up. Cache by
// `locale:decimals` — the only two inputs that affect which formatter we need.
const numberFormatCache = new Map<string, Intl.NumberFormat>();

function getNumberFormat(decimals: number, locale?: string): Intl.NumberFormat {
  const key = `${locale ?? ''}:${decimals}`;
  let fmt = numberFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    numberFormatCache.set(key, fmt);
  }
  return fmt;
}

/**
 * Format a numeric amount as display money — decimals + thousand
 * separators + currency suffix. Safe on non-finite inputs (returns
 * `0.00$`-style zero rather than `NaN$` / `Infinity$`).
 */
export function formatMoney(
  amount: number,
  currency: string = '$',
  { decimals = 2, locale }: FormatMoneyOptions = {},
): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const s = getNumberFormat(decimals, locale).format(n);
  return `${s}${currency}`;
}
